/**
 * chiptune-notes-engine.js — render a chiptune from TRANSCRIBED NOTES over
 * the four NES 2A03 channels (2 × pulse, triangle, noise).
 *
 * Why this exists: the original engine (chiptune-worklet.js) ran a
 * monophonic YIN tracker straight over the input signal and drove ONE
 * oscillator with it. On a real song that is an ill-posed problem — YIN
 * returns a single fundamental per hop, so on a bass + drums + chords + vocal
 * mix it latches onto the lowest fundamental and emits a constant drone.
 * Prod comment #1021 ("сую туда ЛЮБОЙ трек, получаю звуки, от которых кровь
 * из ушей") is exactly that failure mode, and no amount of YIN parameter
 * tuning fixes it — a bake-off against MPM confirmed both algorithms fail
 * identically on polyphonic input.
 *
 * A real chiptune conversion is a TRANSCRIPTION problem, not a pitch-tracking
 * one: get the notes, then voice them across the channels a 2A03 actually
 * has. That is what this module does. Notes come from BasicPitchEngine
 * (polyphonic CNN, already vendored for /audio-to-notes), percussion comes
 * from an onset detector over the source signal, and the voicing follows the
 * classic NES arrangement:
 *
 *   Pulse 1   → lead melody      (via __atnClean.extractMelody)
 *   Pulse 2   → harmony / counter-melody
 *   Triangle  → bass line         (NES triangle has no volume control)
 *   Noise     → drums             (15-bit LFSR, kick/snare/hat by onset band)
 *
 * Everything is rendered sample-by-sample into a Float32Array rather than
 * through OfflineAudioContext oscillators: the NES waveforms are trivial to
 * generate exactly, and it keeps duty cycles / 32-step triangle quantisation
 * / LFSR noise bit-accurate instead of approximated by WebAudio primitives.
 *
 * Style: var/function ES5, to match the rest of public/js and stay parseable
 * on the older Chromium builds still present in prod.
 */
(function(){
'use strict';

// ── Voicing ────────────────────────────────────
// allocate() lives in chiptune-dsp-core.js now — the worker needs it, and it is
// pure note-level work with no DOM dependency. Still exported here: it is part
// of this module's published surface.
function allocate(notes, opts){
    if (!scopeDSP()) return { lead: [], harmony: [], bass: [], bassSplit: 48 };
    return scopeDSP().allocate(notes, opts);
}

/**
 * Percussion onsets for the noise channel.
 *
 * Deliberately NOT a model: two one-pole filters split the signal into a low
 * band (kick) and a high band (snare/hat), and we take peaks of the positive
 * energy difference in each. Cheap, dependency-free, and good enough to give
 * the render a recognisable backbeat — which is most of what makes a chiptune
 * read as "the song" rather than "a melody floating in space".
 *
 * The algorithm itself now lives in chiptune-dsp-core.js so the worker can run
 * it too; this wrapper only unwraps the AudioBuffer (a type the worker has no
 * access to).
 */
function detectPercussion(audioBuffer, opts){
    if (!scopeDSP()) return [];
    return scopeDSP().detectPercussionPcm(audioBuffer.getChannelData(0), audioBuffer.sampleRate, opts || {});
}

// ── Synthesis ──────────────────────────────────────────────────────────
// The sample-level kernel and the voicing both moved to chiptune-dsp-core.js
// (loaded on this page AND importScripts'd by the render worker). What stays
// here is what genuinely needs a document: the worker lifecycle, and turning
// the returned PCM into an AudioBuffer — a main-thread-only API.

function scopeDSP(){ return self.ChiptuneDSP || null; }

// ── Off-thread rendering ───────────────────────────────────────────────
// Prod telemetry (45 d, RU): play 4690 ms / preset 3948 ms average interaction
// delay, 56 rage-clicks on play, 70% of transcriptions never reaching an
// export. All of it one cause — the whole song was synthesised sample-by-sample
// on the thread that paints, and re-synthesised on every parameter change.
// renderAsync() moves that to a worker; render() stays as the fallback for
// browsers where the worker cannot start, so the tool degrades in speed rather
// than in function.

var _worker = null;          // lazily constructed, reused across renders
var _workerState = 'idle';   // idle | starting | ready | dead
var _workerQueue = [];       // callbacks waiting for 'ready'
var _pending = Object.create(null);
var _seq = 0;
var _urls = null;            // { worker, core, clean } — supplied by the page
var _preparedNotes = null;   // note array currently voiced INSIDE the worker
var _syncVoicesCache = { notes: null, voices: null };

function configure(urls){ _urls = urls || null; }

function _failWorker(reason){
    _workerState = 'dead';
    try { if (_worker) _worker.terminate(); } catch(_){}
    _worker = null;
    _preparedNotes = null;   // whatever it had voiced died with it
    // Anything already in flight resolves through the sync path instead of
    // hanging forever — a silent worker is the worst of both worlds.
    var ids = Object.keys(_pending);
    for (var i = 0; i < ids.length; i++) {
        var p = _pending[ids[i]];
        if (p.timer) clearTimeout(p.timer);
        delete _pending[ids[i]];
        try { p.reject(new Error(reason || 'worker_dead')); } catch(_){}
    }
    var q = _workerQueue; _workerQueue = [];
    for (var j = 0; j < q.length; j++) { try { q[j](false); } catch(_){} }
}

function _ensureWorker(cb){
    if (_workerState === 'ready')  return cb(true);
    if (_workerState === 'dead')   return cb(false);
    if (!_urls || !_urls.worker || !_urls.core || typeof Worker === 'undefined') {
        _workerState = 'dead';
        return cb(false);
    }
    _workerQueue.push(cb);
    if (_workerState === 'starting') return;
    _workerState = 'starting';

    try {
        _worker = new Worker(_urls.worker);
    } catch (e) {
        _failWorker('worker_construct_failed');
        return;
    }

    _worker.onerror = function(){ _failWorker('worker_error'); };
    _worker.onmessage = function(e){
        var m = e.data || {};
        if (m.type === 'ready') {
            _workerState = 'ready';
            var q = _workerQueue; _workerQueue = [];
            for (var i = 0; i < q.length; i++) { try { q[i](true); } catch(_){} }
            return;
        }
        if (m.type === 'init_failed') { _failWorker('worker_init_failed'); return; }

        var p = _pending[m.id];
        if (!p) return;                       // superseded render — drop it
        if (m.type === 'progress') { if (p.onProgress) { try { p.onProgress(m.pct); } catch(_){} } return; }
        if (p.timer) clearTimeout(p.timer);
        delete _pending[m.id];
        if (m.type === 'rendered')             p.resolve(m.pcm);
        else if (m.type === 'percussion_done') p.resolve(m.hits);
        else if (m.type === 'prepared')        p.resolve(m.counts);
        else if (m.type === 'lone_voice_done') p.resolve(m.verdict || null);
        else p.reject(new Error(m.error || 'worker_render_failed'));
    };

    /* Watchdog. A worker that never answers is worse than no worker at all:
       every caller awaits a promise that will not settle, and the tool sits on
       a spinner forever with no error to show. Observed for real during
       testing — the worker stayed in 'starting' and the whole prepare step
       deadlocked behind it. Falling back to the synchronous path is merely
       slow; hanging is broken. */
    setTimeout(function(){
        if (_workerState === 'starting') {
            try {
                window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'chiptune_worker_init_timeout' });
            } catch(_){}
            _failWorker('worker_init_timeout');
        }
    }, 10000);

    _worker.postMessage({ type: 'init', core: _urls.core, clean: _urls.clean || null, voice: _urls.voice || null });
}

function _post(payload, transfer, onProgress){
    return new Promise(function(resolve, reject){
        _ensureWorker(function(ok){
            if (!ok) return reject(new Error('worker_unavailable'));
            var id = ++_seq;
            payload.id = id;
            _pending[id] = { resolve: resolve, reject: reject, onProgress: onProgress };
            /* Same reasoning as the init watchdog: a request the worker never
               answers must surface as a rejection (→ synchronous fallback), not
               as a promise that hangs the whole tool. Generous, because a long
               track legitimately takes a while — this is a deadlock guard, not
               a performance budget. */
            _pending[id].timer = setTimeout(function(){
                if (!_pending[id]) return;
                delete _pending[id];
                try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'chiptune_worker_request_timeout', op: payload.type }); } catch(_){}
                reject(new Error('worker_request_timeout'));
            }, 120000);
            try {
                _worker.postMessage(payload, transfer || []);
            } catch (e) {
                clearTimeout(_pending[id].timer);
                delete _pending[id];
                reject(e);
            }
        });
    });
}

function pcmToBuffer(mix, chans, sr){
    var out;
    if (typeof window !== 'undefined' && window.__safeAudioBuffer) {
        out = window.__safeAudioBuffer({ numberOfChannels: chans, length: mix.length, sampleRate: sr });
    } else {
        out = new AudioBuffer({ numberOfChannels: chans, length: mix.length, sampleRate: sr });
    }
    for (var c = 0; c < chans; c++) out.getChannelData(c).set(mix);
    return out;
}

function _renderOpts(opts, length){
    return {
        length:      length,
        sampleRate:  opts.sampleRate || 44100,
        waveType:    opts.waveType | 0,
        volumeSteps: opts.volumeSteps != null ? opts.volumeSteps : 16,
        octaveShift: opts.octaveShift || 0,
        noiseLevel:  opts.noiseLevel != null ? opts.noiseLevel : 0.5,
        percussion:  opts.percussion || [],
        portamento:  opts.portamento !== false,
        arpeggio:    opts.arpeggio !== false,
        arpStepSec:  opts.arpStepSec || 0,
        /* Маска каналов идёт ЗДЕСЬ, а не в двух местах: этот объект — то, что
           одновременно уходит в воркер и передаётся синхронному пути, поэтому
           два лейна не могут зазвучать по-разному. */
        mute:        opts.mute || null,
    };
}

/**
 * renderAsync(notes, opts) → Promise<AudioBuffer>
 *
 * Same output as render(), computed off-thread. opts.onProgress(pct) fires as
 * the worker walks the channels; opts.signal (an AbortSignal) cancels a render
 * that a newer parameter change has superseded.
 *
 * Falls back to the synchronous path — transparently — whenever the worker is
 * unavailable, so no browser loses the feature, only the smoothness.
 */
function renderAsync(notes, opts){
    opts = opts || {};
    var sr       = opts.sampleRate || 44100;
    var duration = opts.duration || 0;
    var chans    = opts.channels || 2;
    if (!duration) return Promise.reject(new Error('chiptune_render_no_duration'));

    var length = Math.ceil(duration * sr);

    return _prepare(notes || [])
        .then(function(){
            return _post({ type: 'render', opts: _renderOpts(opts, length) }, null, opts.onProgress);
        })
        .then(function(pcm){
            if (opts.signal && opts.signal.aborted) throw new Error('aborted');
            return pcmToBuffer(pcm, chans, sr);
        })
        .catch(function(err){
            if (err && err.message === 'aborted') throw err;
            // Worker unavailable/failed — do it here rather than not at all.
            if (!scopeDSP()) throw err;
            var mix = scopeDSP().renderMix(_syncVoices(notes || []), _renderOpts(opts, length));
            return pcmToBuffer(mix, chans, sr);
        });
}

/* Voice the notes INSIDE the worker, once per note set.
   allocate() is ~600 ms on a 4-minute transcription and is a pure function of
   the notes — it reads no render parameter. Re-sending the notes on every
   preset change would re-pay that cost AND structured-clone a few thousand
   note objects for nothing. Reference equality is the right test here: the
   tool replaces the whole array when a new file is transcribed. */
function _prepare(notes){
    if (_preparedNotes === notes) return Promise.resolve(true);
    return _post({ type: 'prepare', notes: notes }).then(function(){
        _preparedNotes = notes;
        return true;
    });
}

/* Main-thread voicing for the fallback path, cached the same way so a browser
   without workers still does not re-voice on every preset click. */
function _syncVoices(notes){
    if (_syncVoicesCache.notes !== notes) {
        _syncVoicesCache = { notes: notes, voices: scopeDSP().allocate(notes, {}) };
    }
    return _syncVoicesCache.voices;
}

/**
 * detectPercussionAsync(audioBuffer) → Promise<hits[]>
 *
 * The onset walk is a second full pass over the signal — on a 4-minute file it
 * is another multi-second main-thread block, and it runs at exactly the moment
 * the user is waiting on the transcription. Copy the channel out and hand it
 * over (the copy is required: the AudioBuffer's own storage cannot be detached).
 */
function detectPercussionAsync(audioBuffer, opts){
    var src = audioBuffer.getChannelData(0);
    var copy = new Float32Array(src.length);
    copy.set(src);
    return _post({ type: 'percussion', pcm: copy, sampleRate: audioBuffer.sampleRate }, [copy.buffer])
        .catch(function(){ return detectPercussion(audioBuffer, opts); });
}

/**
 * loneVoiceAsync(audioBuffer) → Promise<verdict|null>
 *
 * VoiceCoverMixDetect.analyzeLoneVoice in the render worker: «is this a voice
 * with no music to remove?». Resolves null when it cannot answer (no worker,
 * scripts not configured, an error) — never rejects, because the only use is
 * to SKIP a vocal separation, and «don't know» means separate as before.
 */
function loneVoiceAsync(audioBuffer){
    var src = audioBuffer.getChannelData(0);
    var copy = new Float32Array(src.length);
    copy.set(src);
    return _post({ type: 'lone_voice', pcm: copy, sampleRate: audioBuffer.sampleRate }, [copy.buffer])
        .catch(function(){ return null; });
}

/**
 * crushAsync(audioBuffer, opts) → Promise<AudioBuffer>
 *
 * The lo-fi lane: degrade the ORIGINAL signal rather than rebuild it from
 * notes. Written piece by piece straight into the result, yielding between
 * pieces so the thread that paints never freezes (one pass per sample per
 * channel — 21 M iterations on a 4-minute stereo file).
 *
 * It used to copy every channel whole to hand it to the worker, get a second
 * whole array back and build a third: four full copies of the track. A
 * 6-minute stereo song on an Android phone (Sentry N5, 29.09.2026) failed at
 * `new Float32Array(src.length)` right after the result buffer itself had
 * failed once — the result is the one allocation the pass really needs.
 */
function crushAsync(audioBuffer, opts){
    opts = opts || {};
    var D = scopeDSP();
    if (!D || typeof D.crushInto !== 'function') return Promise.reject(new Error('chiptune_dsp_core_missing'));
    var sr = audioBuffer.sampleRate;
    var chans = Math.min(2, audioBuffer.numberOfChannels) || 1;
    var len = audioBuffer.length;
    var out;
    try {
        out = (typeof window !== 'undefined' && window.__safeAudioBuffer)
            ? window.__safeAudioBuffer({ numberOfChannels: chans, length: len, sampleRate: sr })
            : new AudioBuffer({ numberOfChannels: chans, length: len, sampleRate: sr });
    } catch (e) {
        return Promise.reject(e);
    }
    var PIECE = 1 << 18;   // 262 144 samples ≈ 6 s at 44.1 kHz; a slice is ~12 ms of work
    var c = 0, at = 0;
    function yieldTick(){
        return (typeof window !== 'undefined' && window.__yieldTick)
            ? window.__yieldTick(0)
            : new Promise(function(r){ setTimeout(r, 0); });
    }
    return new Promise(function(resolve, reject){
        (function slice(){
            try {
                var t0 = Date.now();
                while (c < chans && Date.now() - t0 < 12) {
                    var end = Math.min(len, at + PIECE);
                    D.crushInto(audioBuffer.getChannelData(c), out.getChannelData(c), at, end, sr, opts);
                    at = end;
                    if (at >= len) { c++; at = 0; }
                }
                if (c >= chans) { resolve(out); return; }
                yieldTick().then(slice, reject);
            } catch (err) { reject(err); }
        })();
    });
}

/**
 * render(notes, opts) → AudioBuffer  (synchronous, main thread)
 *
 * Kept for the fallback path and for parity testing against renderAsync.
 * New call sites should use renderAsync.
 */
function render(notes, opts){
    opts = opts || {};
    var sr       = opts.sampleRate || 44100;
    var duration = opts.duration || 0;
    var chans    = opts.channels || 2;
    if (!duration) throw new Error('chiptune_render_no_duration');
    if (!scopeDSP()) throw new Error('chiptune_dsp_core_missing');

    var length = Math.ceil(duration * sr);
    var voices = allocate(notes || [], opts);
    var mix    = scopeDSP().renderMix(voices, _renderOpts(opts, length));
    return pcmToBuffer(mix, chans, sr);
}

window.ChiptuneNotesEngine = {
    render: render,
    crushAsync: crushAsync,
    renderAsync: renderAsync,
    allocate: allocate,
    detectPercussion: detectPercussion,
    detectPercussionAsync: detectPercussionAsync,
    loneVoiceAsync: loneVoiceAsync,
    configure: configure,
    workerState: function(){ return _workerState; },
};

})();
